import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { FloppyDisk, ArrowSquareOut, Eye, Warning } from "@phosphor-icons/react";
import WhatsAppBubble from "@/components/operacao/WhatsAppBubble";

/**
 * Templates tab — message style references that the orchestrator
 * consumes as INSPIRATION (never literal copy). All six fields live
 * in `inbound_settings` and are managed via /api/reception/settings.
 *
 * Critical UX rule: every textarea must include a clear hint that
 * the AI uses these as "tone reference", not as the message to send
 * verbatim. The grounding layer still forbids price/hour/promo here.
 */

const FIELDS = [
  {
    key: "welcome_template_customer",
    label: "Boas-vindas — cliente conhecido",
    hint: "Primeiro contato com cliente já cadastrado. Variáveis: {nome_cliente}, {saudacao}.",
    placeholder: "Olá {nome_cliente}! Que bom te ver de novo aqui.",
    rows: 3,
  },
  {
    key: "welcome_template_lead",
    label: "Boas-vindas — novo lead",
    hint: "Primeiro contato com quem nunca conversou com você. Sem citar preço ou horário.",
    placeholder: "Oi! Aqui é da equipe. Como vou te ajudar hoje?",
    rows: 3,
  },
  {
    key: "handoff_template",
    label: "Handoff humano",
    hint: "Frase usada quando a IA chama o atendente humano.",
    placeholder: "Vou passar você para nossa equipe agora mesmo.",
    rows: 2,
  },
  {
    key: "appointment_confirmation",
    label: "Confirmação de agendamento",
    hint: "Estilo da confirmação. A IA injeta data/hora dinamicamente.",
    placeholder: "Pronto! Está confirmado. Te esperamos em breve.",
    rows: 2,
  },
  {
    key: "post_service_message",
    label: "Mensagem pós-atendimento",
    hint: "Tom usado para acompanhar o cliente após o serviço concluído.",
    placeholder: "Esperamos que tenha gostado do atendimento.",
    rows: 2,
  },
  {
    key: "missed_appointment_followup",
    label: "Follow-up de falta",
    hint: "Mensagem cordial quando o cliente não compareceu.",
    placeholder: "Sentimos sua falta hoje. Quer remarcar?",
    rows: 2,
  },
];

export default function TemplatesTab({ onChange, onToast }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/reception/settings");
      setData(res.data || {});
    } catch (e) {
      onToast?.("Erro ao carregar templates.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Iter-71.1 — `{agent_signature}` is the COMPLETE self-intro line
  // ("Me chamo D. Power"). Operators no longer need to type the prefix.
  // When no name / brand_agent / silent → empty (line stripped at render).
  const previewVars = {
    company_name: data.company_name || "sua empresa",
    agent_name: data.agent_name || "AgentUS",
    agent_signature: data.agent_name ? `Me chamo ${data.agent_name}` : "",
    agent_signature_short: data.agent_name ? `— ${data.agent_name}` : "— AgentUS",
  };

  /**
   * Pre-process the template the same way render_welcome_for_lead does:
   *   1. Dedupe legacy "Me chamo {agent_signature}" → "{agent_signature}"
   *      so we don't ship "Me chamo Me chamo D. Power".
   *   2. When signature is empty, drop any standalone {agent_signature}
   *      line so the WhatsApp bubble doesn't render a blank gap.
   */
  function applyTemplatePreprocessing(raw) {
    if (!raw) return raw;
    let body = raw.replace(
      /^([ \t]*)me chamo[ \t]+\{agent_signature\}/gim,
      "$1{agent_signature}",
    );
    if (!previewVars.agent_signature) {
      body = body.replace(/^[ \t]*\{agent_signature\}[ \t]*\n?/gim, "");
    }
    return body;
  }

  /**
   * Surface literal brand references in the body — covering BOTH cases:
   *   1. The operator typed the exact current company name (will drift
   *      silently after the next rename).
   *   2. The operator left an OLD brand name from a previous version
   *      of the template (the original bug: body says "Link Data" while
   *      Identity now says "Link data auto").
   *
   * Heuristic stack (cheap, deterministic, no LLM):
   *   - Pull every "brand-like" candidate from the body:
   *     a) text inside *bold*  (operators decorate the brand)
   *     b) CapitalizedBigrams (e.g. "Link Data", "Auto Care")
   *   - Filter out common UI/menu words ("Agendamento", "Orçamento"…)
   *     so highlighting business actions doesn't trigger noise.
   *   - Filter out the agent's name (handled by a separate check).
   *   - If the body already uses `{company_name}` we trust it — no alert.
   *
   * Returns the FIRST suspicious candidate so the UI shows a single
   * focused chip (deeper drift can be fixed in a second pass).
   */
  const COMMON_BRAND_NOISE = useMemo(
    () =>
      new Set([
        "Agendamento", "Orçamento", "Orcamento", "Revisão", "Revisao",
        "Pós-Venda", "Pos Venda", "Atendimento", "Suporte",
        "Boa Tarde", "Boa Noite", "Bom Dia",
      ].map((s) => s.toLowerCase())),
    []
  );

  function findLiteralCompanyMention(body, agentName) {
    if (!body) return null;
    // Aceita ambos os aliases (PT-BR novo + EN legado).
    if (/\{nome_empresa\}|\{company_name\}/i.test(body)) return null;

    const lowerAgent = (agentName || "").trim().toLowerCase();
    const candidates = [];

    // a) Bold-decorated strings: *Foo* / *Foo Bar*
    const boldRe = /\*([^*\n]{2,40})\*/g;
    let m;
    while ((m = boldRe.exec(body)) !== null) {
      const inner = m[1].trim();
      // skip pure-lowercase / verbs like "agendamento" decorated in bold
      if (/^[a-zà-ÿ]+$/i.test(inner) && inner[0] === inner[0].toLowerCase()) {
        continue;
      }
      candidates.push(inner);
    }

    // b) Capitalised bigrams (e.g. "Link Data", "Auto Care")
    //    Matches two consecutive words where each starts uppercase.
    const bigramRe = /\b([A-ZÀ-Ý][a-zà-ÿ]+)\s+([A-ZÀ-Ý][a-zà-ÿ]+)\b/g;
    while ((m = bigramRe.exec(body)) !== null) {
      candidates.push(`${m[1]} ${m[2]}`);
    }

    for (const c of candidates) {
      const lower = c.toLowerCase();
      if (COMMON_BRAND_NOISE.has(lower)) continue;
      if (lowerAgent && lower === lowerAgent) continue;
      // Heuristic: bigram must have at least one short word ≥ 3 chars
      // and not be a generic greeting.
      if (lower.startsWith("seja ") || lower.startsWith("muito ")) continue;
      return { literal: c };
    }
    return null;
  }

  /**
   * Replace a literal mention (and optional surrounding markdown
   * markers) with the variable form. Idempotent: replaces only the
   * first occurrence so the operator can review further matches.
   */
  function applyVariableFix(fieldKey, varName, hit) {
    setData((prev) => {
      const before = prev[fieldKey] || "";
      const escaped = hit.literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(`[*_~]?${escaped}[*_~]?`, "i");
      const next = before.replace(re, `{${varName}}`);
      return { ...prev, [fieldKey]: next };
    });
  }

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        FIELDS.map((f) => [f.key, data[f.key] ?? ""])
      );
      await api.put("/reception/settings", payload);
      onToast?.("Templates atualizados.");
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-400">Carregando…</div>;

  return (
    <div className="space-y-6" data-testid="templates-tab">
      <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
        <p className="text-[12px] text-slate-600 leading-relaxed">
          <strong className="text-slate-900">Estilo de mensagem.</strong> A IA usa estes textos como
          inspiração de tom — nunca como cópia literal. Nada de citar preço, horário ou oferta
          aqui (esses dados têm aba própria e são consultados em tempo real).
        </p>
      </div>

      <div className="space-y-8">
        {FIELDS.map((f) => {
          const body = data[f.key] || "";
          const literalBrand = findLiteralCompanyMention(body, data.agent_name);
          return (
          <div
            key={f.key}
            data-testid={`template-field-${f.key}`}
            className="grid lg:grid-cols-[1fr_minmax(280px,360px)] gap-4 lg:gap-6 pb-6 border-b border-slate-50 last:border-0 last:pb-0"
          >
            {/* ─── Editor ─── */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {f.label}
              </label>
              <p className="text-[12px] text-slate-400 -mt-0.5">{f.hint}</p>
              <textarea
                value={body}
                onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                rows={f.rows}
                placeholder={f.placeholder}
                className="w-full px-3 py-2 rounded-2xl border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none resize-none font-mono"
                data-testid={`template-input-${f.key}`}
              />

              {/* Literal-brand warning — fires when the body cites a
                  brand-like string (bold-decorated or capitalised
                  bigram) instead of using {nome_empresa}. Catches both
                  fresh literals AND stale ones left from renames. */}
              {literalBrand && (
                <div className="space-y-1.5" data-testid={`template-literal-warnings-${f.key}`}>
                  <div className="flex items-start gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-[12px] text-slate-800">
                    <Warning size={13} weight="bold" className="mt-0.5 shrink-0 text-slate-500" />
                    <div className="flex-1 leading-relaxed">
                      Você citou <span className="font-semibold">{literalBrand.literal}</span> direto no texto.
                      Trocando por <code className="bg-slate-200 px-1 rounded">{"{nome_empresa}"}</code>, o nome
                      acompanha automaticamente o que você define em <span className="font-medium">Identidade</span>
                      {" "}— sem ficar duas grafias diferentes na mesma mensagem.
                    </div>
                    <button
                      type="button"
                      onClick={() => applyVariableFix(f.key, "nome_empresa", literalBrand)}
                      data-testid={`template-fix-brand-${f.key}`}
                      className="shrink-0 px-2.5 py-1 rounded-full bg-slate-900 text-white text-[11px] font-medium hover:bg-slate-800 transition-colors"
                    >
                      Aplicar variável
                    </button>
                  </div>
                </div>
              )}

              <p className="text-[11px] text-slate-400 leading-relaxed">
                Formatação WhatsApp: <code className="bg-slate-100 px-1 rounded">*negrito*</code>,
                {" "}<code className="bg-slate-100 px-1 rounded">_itálico_</code>,
                {" "}<code className="bg-slate-100 px-1 rounded">~riscado~</code>.
                Use <code className="bg-slate-100 px-1 rounded">{"{nome_cliente}"}</code>,
                {" "}<code className="bg-slate-100 px-1 rounded">{"{saudacao}"}</code>,
                {" "}<code className="bg-slate-100 px-1 rounded">{"{nome_empresa}"}</code>,
                {" "}<code className="bg-slate-100 px-1 rounded">{"{atendente}"}</code>.
              </p>
            </div>

            {/* ─── Preview no WhatsApp ─── */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">
                <Eye size={11} weight="bold" /> Preview no WhatsApp
              </div>
              <div className="bg-[#efeae2] rounded-2xl p-3 min-h-[110px] flex flex-col justify-center"
                   style={{
                     backgroundImage:
                       "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.04) 1px, transparent 0)",
                     backgroundSize: "12px 12px",
                   }}
                   data-testid={`template-preview-${f.key}`}
              >
                <WhatsAppBubble
                  text={applyTemplatePreprocessing(body || f.placeholder)}
                  vars={previewVars}
                  testId={`wa-bubble-${f.key}`}
                />
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Advanced — telas legadas que ainda guardam fluxos únicos
          (gatilhos do pós-atendimento + textos de cobrança/recibo).
          Serão absorvidas em iterações futuras (Phase 4 cont.). */}
      <div
        className="rounded-2xl border border-dashed border-slate-200 p-4 space-y-2"
        data-testid="templates-advanced"
      >
        <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
          Avançado — fluxos específicos
        </p>
        <p className="text-[12px] text-slate-500">
          Configurações com lógica própria (ainda não absorvidas nesta central).
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Link
            to="/app/pos-atendimento"
            data-testid="templates-link-postservice"
            className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-900 text-slate-700 hover:text-slate-900 transition-colors"
          >
            Pós-atendimento + Avaliação
            <ArrowSquareOut size={11} />
          </Link>
          <Link
            to="/app/mensagens/automaticas"
            data-testid="templates-link-automatic"
            className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-900 text-slate-700 hover:text-slate-900 transition-colors"
          >
            Textos de cobrança e recibo
            <ArrowSquareOut size={11} />
          </Link>
        </div>
      </div>

      <div className="pt-4 border-t border-slate-100 flex items-center justify-end">
        <button
          type="button"
          data-testid="templates-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-medium disabled:opacity-50"
        >
          <FloppyDisk size={14} weight="bold" />
          {saving ? "Salvando…" : "Salvar templates"}
        </button>
      </div>
    </div>
  );
}
